import { encodeAbiParameters, parseAbiParameters, type Address } from 'viem'
import { Call } from '../../../utils/multicall'
import { isFailedCall } from '../../../utils/multicall'
import {
  decodeLensRows,
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'
import { predictInverseEscrow } from './userCallBuild'

/** 4 x uint256 DBR tail precedes the row count. @see `PositionLens.inverse` */
const HEADER_BYTES = 128
/**
 * Words per row: escrow balance, debt, creditLimit, withdrawalLimit.
 *
 * NOT 3, which is what `PositionLens.inverse`'s layout note claims — `_inverseRow`
 * packs FOUR words. The comment is wrong and the code is right; a decoder built
 * from the prose would reject every non-empty blob on a length check. Trust the
 * emitter.
 */
const WORDS = 4

export const usesInverseLens = (chainId: string): boolean =>
  getPositionLensFor(chainId, LENS_FAMILY.INVERSE) !== undefined

/**
 * ONE call instead of 4 per market plus the 4-call DBR tail.
 *
 * The escrow address is CREATE2-predicted off-chain and passed in, exactly as the
 * walk does — a user who never touched a market has no contract at that address,
 * so the balance read fails and parses as 0, which is the right answer.
 *
 * The DBR block is CHAIN-WIDE and emitted unconditionally: FiRM charges interest
 * by burning DBR against the account's whole debt, and a ZERO-runway borrower is
 * the case that matters most — they can be force-replenished at 54.75 % APR every
 * block. Gating that block on having a position would hide precisely the accounts
 * that need it.
 */
export const buildInverseLensCall = (
  chainId: string,
  markets: { address: string; escrowImplementation: string }[],
  dbr: string,
  account: string,
): Call[] => {
  const lens = getPositionLensFor(chainId, LENS_FAMILY.INVERSE)
  if (!lens || !dbr || markets.some((m) => !m.address || !m.escrowImplementation)) {
    return []
  }
  const payload = encodeAbiParameters(
    [
      {
        type: 'tuple[]',
        components: [
          { name: 'market', type: 'address' },
          { name: 'escrow', type: 'address' },
        ],
      },
      { type: 'address' },
    ],
    [
      markets.map((m) => ({
        market: m.address as Address,
        escrow: predictInverseEscrow(
          m.address as Address,
          m.escrowImplementation as Address,
          account as Address,
        ),
      })) as any,
      dbr as Address,
    ],
  )
  return [
    {
      address: lens,
      name: 'getUserData',
      params: [[{ family: LENS_FAMILY.INVERSE, payload }], account],
      abi: PositionLensAbi as any,
    },
  ]
}

/**
 * Rebuild the walk's flat array: `markets x 4` slots then the 4 DBR slots.
 *
 * A market the lens did not emit is filled with zeros, which is what the walk
 * returns for an untouched market — its escrow has no code, so `balance()` fails
 * under `allowFailure` and parses as 0.
 */
export const rebuildInverseData = (
  blobs: unknown,
  marketCount: number,
): any[] | undefined => {
  if (isFailedCall(blobs) || !Array.isArray(blobs) || blobs.length !== 1) {
    return undefined
  }
  let decoded
  try {
    decoded = decodeLensRows(blobs[0] as string, WORDS, HEADER_BYTES, 0)
  } catch {
    return undefined
  }
  const { header, rows } = decoded
  const out: any[] = new Array(marketCount * 4).fill(0n)
  for (const r of rows) {
    if (r.index >= marketCount) return undefined
    for (let k = 0; k < 4; k++) out[r.index * 4 + k] = r.words[k]
  }
  const word = (i: number) =>
    BigInt('0x' + Buffer.from(header.slice(i * 32, i * 32 + 32)).toString('hex'))
  // balanceOf, deficitOf, signedBalanceOf, debts — in the walk's order.
  out.push(word(0), word(1), word(2), word(3))
  return out
}
